import type {
  ContactDetails,
  ContactErrors,
} from "../../types/questionnaire";

interface ContactFormFieldsProps {
  details: ContactDetails;
  errors: ContactErrors;
  privacyAccepted: boolean;
  onDetailsChange: (details: ContactDetails) => void;
  onPrivacyChange: (accepted: boolean) => void;
}

const fields: Array<{
  name: keyof ContactDetails;
  label: string;
  type: string;
  autoComplete: string;
  placeholder: string;
  required: boolean;
  inputMode?: "numeric" | "email" | "tel" | "text";
}> = [
  {
    name: "name",
    label: "Nombre",
    type: "text",
    autoComplete: "name",
    placeholder: "Tu nombre",
    required: true,
  },
  {
    name: "age",
    label: "Edad",
    type: "number",
    autoComplete: "off",
    placeholder: "Tu edad",
    required: true,
    inputMode: "numeric",
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    autoComplete: "email",
    placeholder: "nombre@email.com",
    required: true,
    inputMode: "email",
  },
  {
    name: "whatsapp",
    label: "WhatsApp",
    type: "tel",
    autoComplete: "tel",
    placeholder: "+34 600 000 000",
    required: true,
    inputMode: "tel",
  },
  {
    name: "instagram",
    label: "Instagram",
    type: "text",
    autoComplete: "off",
    placeholder: "@usuario (opcional)",
    required: false,
    inputMode: "text",
  },
];

export function ContactFormFields({
  details,
  errors,
  privacyAccepted,
  onDetailsChange,
  onPrivacyChange,
}: ContactFormFieldsProps) {
  return (
    <div className="questionnaire-contact-fields">
      {fields.map((field) => {
        const errorId = `contact-${field.name}-error`;
        const hasError = Boolean(errors[field.name]);

        return (
          <div
            className={`questionnaire-field questionnaire-field--${field.name}`}
            key={field.name}
          >
            <label htmlFor={`contact-${field.name}`}>
              {field.label}
              {!field.required ? <span>Opcional</span> : null}
            </label>
            <input
              id={`contact-${field.name}`}
              name={field.name}
              type={field.type}
              inputMode={field.inputMode}
              autoComplete={field.autoComplete}
              placeholder={field.placeholder}
              required={field.required}
              min={field.name === "age" ? 16 : undefined}
              max={field.name === "age" ? 100 : undefined}
              maxLength={field.name === "age" ? undefined : 160}
              value={details[field.name]}
              aria-invalid={hasError}
              aria-describedby={hasError ? errorId : undefined}
              onChange={(event) =>
                onDetailsChange({
                  ...details,
                  [field.name]: event.currentTarget.value,
                })
              }
            />
            {hasError ? (
              <p className="questionnaire-error" id={errorId} role="alert">
                {errors[field.name]}
              </p>
            ) : null}
          </div>
        );
      })}

      <div className="questionnaire-privacy">
        <label>
          <input
            id="contact-privacy"
            type="checkbox"
            checked={privacyAccepted}
            required
            aria-invalid={Boolean(errors.privacy)}
            aria-describedby={errors.privacy ? "contact-privacy-error" : undefined}
            onChange={(event) => onPrivacyChange(event.currentTarget.checked)}
          />
          <span className="questionnaire-checkbox" aria-hidden="true" />
          <span>
            He leído y acepto la{" "}
            <a href="/privacidad" target="_blank" rel="noreferrer">
              Política de Privacidad
            </a>
            .
          </span>
        </label>
        {errors.privacy ? (
          <p
            className="questionnaire-error"
            id="contact-privacy-error"
            role="alert"
          >
            {errors.privacy}
          </p>
        ) : null}
      </div>
    </div>
  );
}
